import { ISigner, TPublishOptions, TMutedList, TFeedSubRequest } from '@/types'
import FlexSearch from 'flexsearch'
import { EventTemplate, NostrEvent, VerifiedEvent } from '@nostr/tools/wasm'
import { SubCloser } from '@nostr/tools/abstract-pool'
import { binarySearch, mergeReverseSortedLists } from '@nostr/tools/utils'
import { seenOn } from '@nostr/gadgets/redstore'
import { outboxFilterRelayBatch } from '@nostr/gadgets/outbox'
import { Filter, matchFilters } from '@nostr/tools/filter'
import * as kinds from '@nostr/tools/kinds'
import { pool } from '@nostr/gadgets/global'
import { NostrUser, nostrUserFromEvent } from '@nostr/gadgets/metadata'
import { loadRelayList, loadMuteList } from '@nostr/gadgets/lists'
import { isHex32 } from '@nostr/gadgets/utils'
import i18n from 'i18next'
import { isValidPubkey } from '@/lib/pubkey'
import { tagNameEquals } from '@/lib/tag'
import { isLocalNetworkUrl, normalizeUrl } from '@/lib/url'
import { current, outbox, ready } from './outbox.service'
import { debounce, now } from '@/lib/utils'
import { store } from './store.service'
import { decode } from '@nostr/tools/nip19'
import { AddressPointer, EventPointer } from '@nostr/tools/nip19'
import {
  getReplaceableCoordinateFromEvent,
  isProtectedEvent,
  isReplaceableEvent
} from '@/lib/event'
import { toast } from 'sonner'
import { observe } from 'jotai-effect'
import { getDefaultStore } from 'jotai'
import { currentPubkeyAtom, signerAtom } from '@/atoms/session'
import { relayListAtom } from '@/atoms/lists'

const t = i18n.t.bind(i18n)
const jotaiStore = getDefaultStore()

class ClientService extends EventTarget {
  static instance: ClientService

  signer: ISigner | null = null
  pubkey: string | null = null

  private userIndex = new FlexSearch.Index({
    tokenize: 'full'
  })

  constructor() {
    super()

    observe((get) => {
      get(signerAtom).then((signer) => {
        this.signer = signer
      })
      this.pubkey = get(currentPubkeyAtom)
    })
  }

  public static getInstance(): ClientService {
    if (!ClientService.instance) {
      ClientService.instance = new ClientService()
      ClientService.instance.init()
    }
    return ClientService.instance
  }

  async init() {
    try {
      for (const event of await store.queryEvents({ kinds: [0] })) {
        this.addUsernameToIndex(nostrUserFromEvent(event))
      }
    } catch (err) {
      console.warn('no profiles to index?', err)
    }

    pool.automaticallyAuth = (_relay: string) => async (authEvt: EventTemplate) => {
      const signer = await jotaiStore.get(signerAtom)

      if (signer) {
        const evt = signer.signEvent(authEvt)
        if (!evt) {
          throw new Error('sign event failed')
        }
        return evt as any
      }

      throw new Error("<not logged in, can't automatically auth>")
    }
  }

  async determineTargetRelays(
    event: NostrEvent,
    {
      specifiedRelayUrls,
      additionalRelayUrls,
      includeOurWriteRelays = true,
      includeTheirReadRelays = true,
      additionalMentionPubkeys
    }: TPublishOptions = {}
  ) {
    if (event.kind === kinds.Report) {
      const targetEventId = event.tags.find(tagNameEquals('e'))?.[1]
      if (targetEventId) {
        return this.getSeenEventRelayUrls(targetEventId)
      }
    }

    let relays: string[]
    if (specifiedRelayUrls?.length) {
      relays = specifiedRelayUrls
    } else {
      const _additionalRelayUrls: string[] = additionalRelayUrls ?? []
      if (
        includeTheirReadRelays &&
        event.kind !== kinds.Contacts &&
        event.kind !== kinds.Mutelist
      ) {
        const mentions: string[] = [...(additionalMentionPubkeys ?? [])]
        for (const [tagName, tagValue] of event.tags) {
          if (
            ['p', 'P'].includes(tagName) &&
            !!tagValue &&
            isValidPubkey(tagValue) &&
            !mentions.includes(tagValue)
          ) {
            mentions.push(tagValue)
          }
        }
        if (mentions.length > 0) {
          const relayLists = await Promise.all(mentions.map((pk) => loadRelayList(pk)))
          for (const { items } of relayLists) {
            _additionalRelayUrls.push(
              ...items
                .filter((r) => r.read)
                .map((r) => r.url)
                .slice(0, 3)
            )
          }
        }
      }
      if (
        event.kind === kinds.RelayList ||
        event.kind === kinds.Contacts ||
        event.kind === kinds.FavoriteRelays ||
        event.kind === kinds.BlossomServerList
      ) {
        _additionalRelayUrls.push(...window.hallway.universe.bigRelayUrls)
      }

      const ourWriteRelayUrls = includeOurWriteRelays
        ? (await getDefaultStore().get(relayListAtom))
            .filter((r) => r.write)
            .map((r) => r.url)
            .slice(0, 10)
        : []
      relays = ourWriteRelayUrls.concat(Array.from(new Set(_additionalRelayUrls)) ?? [])
    }

    if (!relays.length) {
      relays.push(...window.hallway.universe.bigRelayUrls)
    }

    return relays
  }

  async publish(evtt: EventTemplate, options: TPublishOptions = {}): Promise<VerifiedEvent | null> {
    const signer = await jotaiStore.get(signerAtom)

    const evt = await signer?.signEvent(evtt)
    if (!evt) return null

    const relays = await this.determineTargetRelays(evt, options)
    this.publishEvent(relays, evt!)

    await this.addEventToCache(evt, isReplaceableEvent(evt.kind) ? evt.created_at : undefined)

    return evt
  }

  async publishEvent(relayUrls: string[], event: NostrEvent) {
    const uniqueRelayUrls = Array.from(new Set(relayUrls.map(normalizeUrl)))
    await new Promise<void>((resolve, reject) => {
      let successCount = 0
      let finishedCount = 0
      const errors: { url: string; error: any }[] = []
      Promise.allSettled(
        uniqueRelayUrls.map(async (url) => {
          // eslint-disable-next-line @typescript-eslint/no-this-alias
          const that = this
          const relay = await pool.ensureRelay(url)
          relay.publishTimeout = 10_000 // 10s
          return relay
            .publish(event)
            .then(() => {
              this.trackEventSeenOn(event.id, relay)
              successCount++
            })
            .catch((error) => {
              if (
                error instanceof Error &&
                error.message.startsWith('auth-required') &&
                !!that.signer
              ) {
                return relay
                  .auth(((authEvt: EventTemplate) => that.signer!.signEvent(authEvt)) as any)
                  .then(() => relay.publish(event))
                  .then(() => {
                    this.trackEventSeenOn(event.id, relay)
                    successCount++
                  })
              } else {
                errors.push({ url, error })
              }
            })
            .finally(() => {
              if (++finishedCount >= uniqueRelayUrls.length) {
                if (successCount > 0) {
                  this.emitNewEvent(event)
                  resolve()
                } else {
                  reject(
                    new AggregateError(
                      errors.map(
                        ({ url, error }) =>
                          new Error(
                            `${url}: ${error instanceof Error ? error.message : String(error)}`
                          )
                      )
                    )
                  )
                }
              }
            })
        })
      )
    })
  }

  emitNewEvent(event: NostrEvent) {
    this.dispatchEvent(new CustomEvent('newEvent', { detail: event }))
    this.addEventToCache(event)
  }

  emitEphemeralEvent(event: NostrEvent) {
    this.dispatchEvent(new CustomEvent('newEvent', { detail: event }))
  }

  emitEventRemoved(eventId: string) {
    this.dispatchEvent(new CustomEvent('eventRemoved', { detail: eventId }))
  }

  async signHttpAuth(url: string, method: string, description = '') {
    const signer = await jotaiStore.get(signerAtom)
    if (!signer) {
      throw new Error('Please login first to sign the event')
    }

    const event = await signer.signEvent({
      content: description,
      kind: kinds.HTTPAuth,
      created_at: now(),
      tags: [
        ['u', url],
        ['method', method]
      ]
    })

    return 'Nostr ' + btoa(JSON.stringify(event))
  }

  subscribeTimeline(
    subRequests: TFeedSubRequest[],
    filterModification: Filter,
    {
      onEvents,
      onNew,
      onClose
    }: {
      onEvents: (events: NostrEvent[], isFinal: boolean) => void
      onNew: (evt: NostrEvent) => void
      onClose?: (url: string, reason: string) => void
    },
    {
      startLogin
    }: {
      startLogin?: () => void
    } = {}
  ): SubCloser {
    let subc: SubCloser
    const abort = new AbortController()

    const localFilters = subRequests
      .filter((req): req is Extract<TFeedSubRequest, { source: 'local' }> => req.source === 'local')
      .map(({ filter }) => ({
        ...filter,
        ...filterModification
      }))

    const relayRequests = subRequests
      .filter(
        (req): req is Extract<TFeedSubRequest, { source: 'relays' }> => req.source === 'relays'
      )
      .map(({ urls, filter }) => ({
        urls,
        filter: {
          ...filter,
          ...filterModification
        }
      }))

    // do local db requests
    let localSyncCallback: () => void
    let localNewCallback: (_: NostrEvent) => void
    const local: Promise<[NostrEvent[], NostrEvent | undefined, string[]]> =
      localFilters.length === 0
        ? Promise.resolve([[], undefined, []])
        : (async () => {
            let newestEvent: NostrEvent | undefined

            // query from local db
            let events: NostrEvent[] = []
            for (let i = 0; i < localFilters.length; i++) {
              const queryEvents = await store.queryEvents(localFilters[i], 5_000)
              const first = queryEvents[0]

              if (first) {
                if (!newestEvent || newestEvent.created_at < first.created_at) {
                  newestEvent = first
                }
              }

              if (events.length === 0) events = queryEvents
              else events = mergeReverseSortedLists(events, queryEvents)
            }

            // a background sync may be happening and we may be interested in it, handle when it ends
            localSyncCallback = debounce(
              async () => {
                for (let i = 0; i < localFilters.length; i++) {
                  const filter = localFilters[i]
                  for (const event of await store.queryEvents(filter, 5_000)) {
                    // check if this isn't already in the sorted array of events
                    const [_, exists] = binarySearch(events, (b) => {
                      if (event.id === b.id) return 0
                      if (event.created_at === b.created_at) return -1
                      return b.created_at - event.created_at
                    })
                    if (!exists) {
                      onNew(event)
                    }
                  }
                }
              },
              2000,
              2800
            )
            current.onsync.push(localSyncCallback)

            // we'll use this for the live query
            const allAuthors = (
              await Promise.all(
                localFilters.map(async (f) => {
                  if (f.authors) return f.authors
                  return []
                })
              )
            ).flat()

            // listen for live updates to local db
            ready().then(() => {
              outbox.live(allAuthors, window.hallway.universe.feedKinds, {
                signal: abort.signal
              })

              localNewCallback = (event: NostrEvent) => {
                if (matchFilters(localFilters, event)) onNew(event)
              }
              current.onnew.push(localNewCallback)

              this.addEventListener(
                'newEvent',
                (evt: Event) => {
                  const event = (evt as CustomEvent<NostrEvent>).detail
                  if (matchFilters(localFilters, event)) onNew(event)
                },
                { signal: abort.signal }
              )
            })

            return [events, newestEvent, allAuthors]
          })()

    // do relay requests
    const network =
      relayRequests.length === 0
        ? Promise.resolve([])
        : new Promise<NostrEvent[]>((resolve) => {
            let eosed = false

            // keep track of this just so we can refer to them for onClose
            const urls: string[] = []

            let events: NostrEvent[] = []
            subc = pool.subscribeMap(
              relayRequests.flatMap(({ urls, filter }) =>
                urls.flatMap((url) => {
                  if (!urls.includes(url)) urls.push(url)
                  return { url, filter }
                })
              ),
              {
                label: 'f-timeline',
                onevent: (evt) => {
                  if (!eosed) {
                    events.push(evt)
                  } else {
                    onNew(evt)
                  }

                  if (localFilters.length) {
                    // if we're querying local filters simultaneously with external relays
                    // that means we want to store the results from the external relays in the local filters
                    // for consistency
                    this.addEventToCache(evt)
                  }
                },
                oneose() {
                  eosed = true
                  resolve(events)
                  events = []
                },
                onauth: (async (authEvt: EventTemplate) => {
                  // already logged in
                  if (this.signer) {
                    const evt = await this.signer!.signEvent(authEvt)
                    if (!evt) {
                      throw new Error('sign event failed')
                    }
                    return evt as VerifiedEvent
                  }

                  // open login dialog
                  if (startLogin) {
                    startLogin()
                  }

                  throw new Error(
                    "<not logged in, can't auth to relay during this.subscribeTimeline>"
                  )
                }) as any,
                onclose(reasons) {
                  if (onClose) {
                    for (let i = 0; i < reasons.length; i++) {
                      const reason = reasons[i]
                      onClose(urls[i], reason.reason)
                    }
                  }
                  resolve(events)
                }
              }
            )
          })

    if (localFilters.length > 0 && relayRequests.length > 0) {
      // if both exist, assume localFilters will load much faster and handle they first
      local.then(([eventsL]) => {
        if (localFilters.length > 1) eventsL.sort((a, b) => b.created_at - a.created_at)
        onEvents(eventsL, false) // not final: will be called again with all the events later
      })
    }

    let preliminarySub: SubCloser | undefined
    if (
      relayRequests.length === 0 &&
      localFilters.length === 1 &&
      (localFilters[0]!.authors?.length || 0) >= 1
    ) {
      // edge case: used mainly on the first time the app is used
      // in case we're fetching a _following feed_ or _profile feed_ solely from the local db but we have nothing
      // (or only very old events) do a temporary fallback relay query here while the sync completes
      local.then(async ([_, newestEvent, allAuthors]) => {
        if (!newestEvent || newestEvent.created_at < Date.now() / 1000 - 60 * 60 * 24 * 7) {
          const events: NostrEvent[] = []
          preliminarySub = pool.subscribeMap(
            await outboxFilterRelayBatch(allAuthors, [{ ...localFilters[0], limit: 10 }], {
              fallbackRelays: window.hallway.universe.bigRelayUrls
            }),
            {
              label: `f-temporary`,
              onevent(event) {
                events.push(event)
              },
              oneose() {
                preliminarySub!.close('preliminary req closed automatically on eose')
                events.sort((a, b) => b.created_at - a.created_at)
                onEvents(events, false) // not final. it will be final when the sync completes
              }
            }
          )

          // now the sync is complete, do the query again
          ready().then(async () => {
            const events: NostrEvent[] = new Array(200)
            let f = 0
            for (let i = 0; i < localFilters.length; i++) {
              for (const event of await store.queryEvents(localFilters[i], 5_000)) {
                events[f] = event
                f++
              }
            }
            events.length = f
            events.sort((a, b) => b.created_at - a.created_at)
            onEvents(events, true)
          })
        }
      })
    }

    // handle local results immediately
    local.then(([eventsL]) => {
      if (localFilters.length > 1) eventsL.sort((a, b) => b.created_at - a.created_at)
      onEvents(eventsL, false) // not final: will be called again with merged results later

      // handle network results and merge
      network.then((eventsN) => {
        if (eventsL.length > 0 && eventsN.length > 0) {
          // we have results both from relays and from local
          eventsN.sort((a, b) => b.created_at - a.created_at)
          const merged = mergeReverseSortedLists(eventsL, eventsN)
          onEvents(merged, true)
        } else if (eventsN.length) {
          // we only have results from relays
          if (relayRequests.length > 1 || relayRequests[0].urls.length > 1) {
            // sort results from relays -- but don't sort if we're querying a single relay,
            // as it will already send things sorted
            // (or in its own custom order that we'll respect because why not)
            eventsN.sort((a, b) => b.created_at - a.created_at)
          }
          onEvents(eventsN, true)
        } else if (eventsL.length) {
          // only local results, no results from relays, just call again as before, but signal completion
          onEvents(eventsL, true)
        } else {
          // no events found, but still need to signal completion
          onEvents([], true)
        }
      })
    })

    return {
      close() {
        onClose = undefined
        abort.abort('<subc>')
        subc?.close?.()
        preliminarySub?.close?.()
        if (localSyncCallback) {
          const idx = current.onsync.indexOf(localSyncCallback)
          if (idx !== -1) {
            current.onsync.splice(idx, 1)
          }
        }
        if (localNewCallback) {
          const idx = current.onnew.indexOf(localNewCallback)
          if (idx !== -1) {
            current.onnew.splice(idx, 1)
          }
        }
      }
    }
  }

  async loadMoreTimeline(
    subRequests: TFeedSubRequest[],
    filterModification: Filter,
    {
      startLogin
    }: {
      startLogin?: () => void
    } = {}
  ): Promise<NostrEvent[]> {
    const abort = new AbortController()

    const localFilters = subRequests
      .filter((req): req is Extract<TFeedSubRequest, { source: 'local' }> => req.source === 'local')
      .map(({ filter }) => ({
        ...filter,
        ...filterModification
      }))

    const relayRequests = subRequests
      .filter(
        (req): req is Extract<TFeedSubRequest, { source: 'relays' }> => req.source === 'relays'
      )
      .map(({ urls, filter }) => ({
        urls,
        filter: {
          ...filter,
          ...filterModification
        }
      }))

    // do relay requests
    const network =
      relayRequests.length === 0
        ? Promise.resolve([])
        : await new Promise<NostrEvent[]>((resolve) => {
            const events: NostrEvent[] = []
            const subc = pool.subscribeMap(
              relayRequests.flatMap(({ urls, filter }) => urls.flatMap((url) => ({ url, filter }))),
              {
                label: 'f-more',
                onevent(evt) {
                  events.push(evt)
                },
                oneose() {
                  subc.close()
                  resolve(events)
                },
                onclose() {
                  resolve(events)
                },

                onauth: (async (authEvt: EventTemplate) => {
                  // already logged in
                  if (this.signer) {
                    const evt = await this.signer!.signEvent(authEvt)
                    if (!evt) {
                      throw new Error('sign event failed')
                    }
                    return evt as VerifiedEvent
                  }

                  // open login dialog
                  if (startLogin) {
                    startLogin()
                    return
                  }

                  throw new Error(
                    "<not logged in, can't auth to relay during this.loadMoreTimeline>"
                  )
                }) as any
              }
            )
          })

    // do local requests
    const local =
      localFilters.length === 0
        ? Promise.resolve([])
        : (async () => {
            // if any local filter targets specific authors, paginate the outbox
            // backwards down to `until` first so the local store actually contains
            // the older events we're about to query (otherwise pagination would
            // just return whatever is already cached)
            const until = filterModification.until
            if (until) {
              const authors = localFilters.flatMap((f) => f.authors ?? [])
              if (authors.length) {
                try {
                  await outbox.before(authors, window.hallway.universe.feedKinds, until, {
                    signal: abort.signal
                  })
                } catch (err) {
                  console.warn('outbox.before failed during loadMoreTimeline', err)
                }
              }
            }

            const events: NostrEvent[] = new Array(200)
            let f = 0
            for (let i = 0; i < localFilters.length; i++) {
              const filter = localFilters[i]
              for (const event of await store.queryEvents(filter, 5_000)) {
                events[f] = event
                f++
              }
            }
            events.length = f
            return events
          })()

    return Promise.all([local, network]).then(([eventsL, eventsN]) => {
      if (eventsL.length > 0 && eventsN.length > 0) {
        eventsL.push(...eventsN)
        eventsL.sort((a, b) => b.created_at - a.created_at)
        return eventsL
      } else if (eventsL.length) {
        if (localFilters.length > 1) eventsL.sort((a, b) => b.created_at - a.created_at)
        return eventsL
      } else if (eventsN.length) {
        if (relayRequests.length > 1 || relayRequests[0]?.urls.length > 1)
          eventsN.sort((a, b) => b.created_at - a.created_at)
        return eventsN
      } else {
        return []
      }
    })
  }

  async attemptDelete(targetEvent: NostrEvent): Promise<void> {
    const signer = await jotaiStore.get(signerAtom)
    if (!signer) throw new Error(t('You need to login first!'))

    if (this.pubkey !== targetEvent.pubkey)
      throw new Error(t('You can only delete your own events!'))

    const evtt = {
      kind: kinds.EventDeletion,
      content: 'Request for deletion of the event.',
      tags: [
        ['k', `${targetEvent.kind}`],
        ['e', targetEvent.id]
      ],
      created_at: now()
    }

    if (isReplaceableEvent(evtt.kind))
      evtt.tags.push(['a', getReplaceableCoordinateFromEvent(targetEvent)])

    const deletionRequest = await signer.signEvent(evtt)
    const seenOn = this.getSeenEventRelayUrls(targetEvent.id, targetEvent)
    const relays = await this.determineTargetRelays(targetEvent, {
      specifiedRelayUrls: isProtectedEvent(targetEvent) ? seenOn : undefined,
      additionalRelayUrls: seenOn
    })

    await this.publishEvent(relays, deletionRequest)
    toast.success(t('Deletion request sent to {{count}} relays', { count: relays.length }))

    await this.removeEventFromCache(targetEvent.id)
    this.emitEventRemoved(targetEvent.id)
  }

  getSeenEventRelays(eventId: string) {
    return Array.from(pool.seenOn.get(eventId)?.values() || [])
  }

  getSeenEventRelayUrls(eventId: string, event?: NostrEvent) {
    const poolUrls = this.getSeenEventRelays(eventId).map((relay) => relay.url)

    // events loaded from the store may have a special list of "seenOn" relays attached
    const relays = event ? seenOn(event) : []
    const combined = new Set([...poolUrls, ...relays])

    return Array.from(combined)
  }

  public getEventHints(eventId: string, event?: NostrEvent): string[] {
    return this.getSeenEventRelayUrls(eventId, event).filter((url) => !isLocalNetworkUrl(url))
  }

  public getEventHint(eventId: string, event?: NostrEvent): string {
    return this.getSeenEventRelayUrls(eventId, event).find((url) => !isLocalNetworkUrl(url)) ?? ''
  }

  trackEventSeenOn(eventId: string, relay: any) {
    const seenOn = pool.seenOn as Map<string, Set<any>>
    let set = seenOn.get(eventId)
    if (!set) {
      set = new Set()
      seenOn.set(eventId, set)
    }
    set.add(relay)
  }

  async fetchEvents(
    urls: string[],
    filter: Filter,
    {
      cache = false
    }: {
      onevent?: (evt: NostrEvent) => void
      cache?: boolean
    } = {}
  ) {
    const relays = urls.filter((url, i) => i === 0 || urls[i - 1] !== url)
    const events: NostrEvent[] = []

    await new Promise<void>((resolve) => {
      pool.subscribeEose(relays, filter, {
        label: 'f-fetch-events',
        maxWait: 10_000,

        onauth: (async (authEvt: EventTemplate) => {
          if (this.signer) {
            const evt = await this.signer!.signEvent(authEvt)
            if (!evt) {
              throw new Error('sign event failed')
            }
            return evt as VerifiedEvent
          }

          throw new Error("<not logged in, can't auth to relay during this.fetchEvents>")
        }) as any,
        onevent: (event: NostrEvent) => {
          events.push(event)
          if (cache) {
            this.addEventToCache(event)
          }
        },
        onclose(_: { url: string; reason: string }[]) {
          resolve()
        }
      })
    })

    return events
  }

  async addEventToCache(event: NostrEvent, replaceableLastAttempt?: number) {
    const result = await store.saveEvent(event, {
      seenOn: Array.from(pool.seenOn.get(event.id) || []).map((relay) => relay.url),
      lastAttempt: replaceableLastAttempt
    })
    if (event.kind === 0) {
      this.addUsernameToIndex(nostrUserFromEvent(event))
    }
    return result
  }

  async removeEventFromCache(eventId: string) {
    return store.deleteEvents([eventId])
  }

  async fetchEvent(
    idOrCode: string | EventPointer | AddressPointer
  ): Promise<NostrEvent | undefined> {
    let filter: Filter | undefined
    let relayHints: string[] = []
    let authorHint: string | undefined
    if (typeof idOrCode === 'string') {
      if (isHex32(idOrCode)) {
        filter = { ids: [idOrCode] }
      } else {
        const { type, data } = decode(idOrCode)
        switch (type) {
          case 'note':
            filter = { ids: [data] }
            break
          case 'nevent':
            filter = { ids: [data.id] }
            if (data.relays) relayHints = data.relays
            if (data.author) authorHint = data.author
            break
          case 'naddr':
            filter = {
              authors: [data.pubkey],
              kinds: [data.kind],
              limit: 1
            }
            authorHint = data.pubkey
            if (data.identifier) {
              filter['#d'] = [data.identifier]
            }
            if (data.relays) relayHints = data.relays
        }
      }
    } else if ('id' in idOrCode) {
      // EventPointer
      filter = { ids: [idOrCode.id] }
      if (idOrCode.relays) relayHints = idOrCode.relays
      if (idOrCode.author) authorHint = idOrCode.author
    } else {
      // AddressPointer
      filter = {
        authors: [idOrCode.pubkey],
        kinds: [idOrCode.kind],
        limit: 1
      }
      authorHint = idOrCode.pubkey
      if (idOrCode.identifier) {
        filter['#d'] = [idOrCode.identifier]
      }
      if (idOrCode.relays) relayHints = idOrCode.relays
    }

    if (!filter) {
      throw new Error(`can't fetch ${typeof idOrCode === 'string' ? idOrCode : 'pointer'}`)
    }

    const now_ = now()
    const isReplaceable =
      filter.kinds?.length === 1 &&
      filter.authors?.length === 1 &&
      (kinds.isReplaceableKind(filter.kinds[0]) ||
        (kinds.isAddressableKind(filter.kinds[0]) && filter['#d']?.length === 1))

    let storedEvent: NostrEvent | undefined
    if (isReplaceable) {
      const dtag = filter['#d']?.[0]
      const [[lastAttempt, event]] = (await store.loadReplaceables([
        [filter.kinds![0], filter.authors![0], dtag]
      ])) as [number | undefined, NostrEvent][]

      const TWO_DAYS = 2 * 24 * 60 * 60
      const isEmpty = event?.id === '0'.repeat(64)

      if (event) {
        if (isEmpty) {
          if (lastAttempt !== undefined && lastAttempt > now_ - TWO_DAYS) {
            return undefined // recent attempt, nothing found, skip
          }
        } else if (lastAttempt !== undefined && lastAttempt > now_ - TWO_DAYS) {
          return event // fresh enough
        }
      }

      storedEvent = !isEmpty && event ? event : undefined
    } else if (filter.ids?.length === 1) {
      for (const event of await store.queryEvents(filter, 1)) {
        return event
      }
    } else {
      throw new Error(`can only fetch replaceables or events by id, got ${filter}`)
    }

    // couldn't find anything in local database, try relays now
    //

    // start fetching this here so it's finished later when we need it
    const authorRelays = authorHint && loadRelayList(authorHint)

    // try the relay hints first
    if (relayHints.length) {
      relayHints = relayHints.map(normalizeUrl)
      const event = await pool.get(relayHints, filter, { label: 'f-specific-event-1' })
      if (event) {
        this.addEventToCache(event, isReplaceable ? now_ : undefined)
        return event
      }
    }

    // at this point we may already have our author hints so let's try those
    let authorRelaysUrls: string[] = []
    if (authorRelays) {
      authorRelaysUrls = (await authorRelays).items
        .filter((r) => r.write && !relayHints.includes(r.url))
        .map((r) => r.url)
      if (authorRelaysUrls.length) {
        const event = await pool.get(authorRelaysUrls, filter, { label: 'f-specific-event-2' })
        if (event) {
          this.addEventToCache(event, isReplaceable ? now_ : undefined)
          return event
        }
      }
    }

    // if we got nothing or there were no hints, try the big relays (except the ones we've already tried)
    const bigRelayHints = window.hallway.universe.bigRelayUrls.filter(
      (br: string) => !(relayHints.includes(br) || authorRelaysUrls.includes(br))
    )
    bigRelayHints.push('wss://cache2.primal.net/v1')
    if (bigRelayHints.length) {
      const event = await pool.get(bigRelayHints, filter, { label: 'f-specific-event-3' })
      if (event) {
        this.addEventToCache(event, isReplaceable ? now_ : undefined)
        return event
      }
    }

    if (isReplaceable) {
      if (storedEvent) {
        // have old cached event, return it as fallback
        return storedEvent
      }

      // save our failed attempt
      const dtag = filter['#d']?.[0]
      store.saveEvent(
        {
          id: '0'.repeat(64),
          pubkey: filter.authors![0],
          kind: filter.kinds![0],
          sig: '0'.repeat(128),
          tags: dtag ? ([['d', dtag]] as string[][]) : [],
          created_at: 0,
          content: ''
        } as NostrEvent,
        { lastAttempt: now_ }
      )
    }

    return undefined
  }

  async searchProfiles(relayUrls: string[], filter: Filter): Promise<NostrUser[]> {
    const events = await pool.querySync(
      relayUrls,
      {
        ...filter,
        kinds: [kinds.Metadata]
      },
      { label: 'f-search-profiles', maxWait: 5_000 }
    )

    const profiles = events.map(nostrUserFromEvent)
    await Promise.allSettled(profiles)
    return profiles
  }

  searchPubKeysFromLocal(query: string, limit: number = 100): string[] {
    return this.userIndex.search(query, { limit }) as string[]
  }

  public addUsernameToIndex(profile: NostrUser) {
    if (this.userIndex.contain(profile.pubkey)) return

    const text = [
      profile.metadata.display_name?.trim() ?? '',
      profile.metadata.name?.trim() ?? '',
      profile.metadata.nip05
        ?.split?.('@')
        ?.map?.((s: string) => s.trim())
        ?.join?.(' ') ?? '',
      profile.metadata.about
    ].join(' ')
    if (!text) return

    this.userIndex.add(profile.pubkey, text)
  }

  async fetchMuteList(
    pubkey: string,
    forceUpdate: boolean | NostrEvent = false
  ): Promise<{ list: TMutedList; event: NostrEvent | null }> {
    const muteList: TMutedList = {
      public: [],
      private: []
    }

    const result = await loadMuteList(pubkey, [], forceUpdate)
    console.debug('[mute] loadMuteList result:', {
      eventId: result.event?.id,
      createdAt: result.event?.created_at,
      itemsCount: result.items.length
    })

    muteList.public = result.items
      .filter((item) => item.label === 'pubkey')
      .map((item) => item.value)

    if (
      result.event?.content.length &&
      result.event.pubkey === this.pubkey &&
      this.signer?.nip04Decrypt
    ) {
      try {
        const timeout = new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('nip04Decrypt timeout')), 5000)
        )
        const plaintext = await Promise.race([
          result.event.content.includes('?iv=')
            ? this.signer.nip04Decrypt(pubkey, result.event.content)
            : this.signer?.nip44Decrypt?.(pubkey, result.event.content),
          timeout
        ])

        if (plaintext) {
          let privateTags: string[][] = []
          try {
            privateTags = JSON.parse(plaintext)
            if (!Array.isArray(privateTags) || !privateTags.every(Array.isArray)) {
              throw new Error()
            }
          } catch {
            privateTags = []
          }

          for (let i = 0; i < privateTags.length; i++) {
            const tag = privateTags[i]
            if (tag[0] === 'p' && tag.length >= 2 && isHex32(tag[1])) {
              muteList.private.push(tag[1])
            }
          }
        }
      } catch (e) {
        console.error('[mute] nip04Decrypt failed:', e)
      }
    }

    console.debug('[mute] fetchMuteList result:', {
      public: muteList.public.length,
      private: muteList.private.length
    })
    return { list: muteList, event: result.event ?? null }
  }
}

const instance = ClientService.getInstance()

export default instance
